/**
 * Teams Domain
 * Types, store, handlers and utilities for team management
 */

import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import type { MessageType } from '../constants/op-codes';
import { currentSeconds } from '../utils/use-now-seconds';

// ==================== TYPES ====================

/**
 * TeamCostModifiers holds computed cost modifier percentages from deals/policies.
 * Values are percentages: -0.25 = 25% reduction, 0.25 = 25% increase
 */
interface TeamCostModifiers {
  /** Percentage modifier for airplane purchase costs (e.g., -0.20 = 20% reduction) */
  plane_cost: number;
  /** Percentage modifier for landing fees (e.g., 0.25 = 25% increase) */
  landing_fee: number;
  /** Absolute modifier for flight time in minutes (e.g., -15 = 15 minutes reduction) */
  flight_time: number;
  /** Percentage modifier for turnaround time (e.g., 2.00 = +200% turnaround time) */
  turnaround_time: number;
}

/**
 * Team represents a player team in the airline management game.
 * This structure holds all core team data including members, resources,
 * and strategic information.
 */
export interface Team {
  /** Incremental, unique identifier for the team in this match (1-50) */
  team_id: number;
  /** Unique 2-letter code for the team (e.g., "AA", "BB", "CC") */
  team_code: string;
  /** Player names of all team members */
  members: string[];
  /** Player IDs of all team members */
  member_ids: string[];
  /** Each player's 3D pointer location around the globe */
  pointers: { x: number; y: number; z: number }[];
  /** Chosen team name */
  name: string;
  /** Filename of the team's logo image */
  logo: string;
  /** Team's hex color code (e.g., "#FF5733") */
  color: string;
  /** City ID where the team's headquarters is located */
  hq: number;
  hq_established?: boolean;
  influence_seconds?: number;
  /**
   * When `influence_seconds` was read, in this client's Unix seconds: the
   * clock counts on from it while the team accrues (influenceSecondsNow). Set
   * by the store, never sent by the server.
   */
  influence_seconds_at?: number;
  influence_accruing?: boolean;
  lifetime_passenger_miles?: number;
  /** Millions of currency on hand (e.g., 5.5 = $5.5M) */
  cash: number;
  /** Team's influence points */
  influence: number;
  /** Total kilometers flown by this team since inception */
  lifetime_km_flown: number;
  /** Total passengers carried by this team since inception */
  lifetime_passengers: number;
  /** A permanent cohort ID for storage and leaderboards */
  cohort_id_backend: string;
  /** Map of innovation IDs that this team has researched/unlocked */
  unlocked_innovations?: Record<string, boolean>;
  /** Computed cost modifiers from deals/policies (populated by server) */
  cost_modifiers?: TeamCostModifiers;
}

// ==================== STORE ====================

interface TeamsState {
  teams: Record<string, Team>; // Map of cohort_id -> Team
  setTeams: (teams: Record<string, Team>) => void;
  updateTeam: (cohortId: string, team: Team) => void;
  updateTeamResources: (teamId: number, newCash: number, newInfluence: number, lifetimePassengers?: number, passengerMiles?: number, influenceSeconds?: number) => void;
  reset: () => void;
}

const initialState = {
  teams: {},
};

/**
 * A team as the server sent it carries its seconds without when they were
 * read: they were read now. A team copied from the store keeps its own stamp,
 * so a change to another of its fields does not move its clock back.
 */
function stampInfluenceSeconds(team: Team, at: number): Team {
  if (team.influence_seconds === undefined || team.influence_seconds_at !== undefined) return team;
  return { ...team, influence_seconds_at: at };
}

/**
 * Teams Store
 * Manages team state for the current match
 */
export const useTeamsStore = create<TeamsState>()(
  devtools(
    (set) => ({
      ...initialState,

      /**
       * Set teams data (replaces existing)
       */
      setTeams: (teams) => {
        console.log('[TeamsStore] Setting teams data:', Object.keys(teams).length, 'teams');
        const at = currentSeconds();
        const stamped = Object.fromEntries(Object.entries(teams).map(([cohortId, team]) => [cohortId, stampInfluenceSeconds(team, at)]));
        set({ teams: stamped }, false, 'setTeams');
      },

      /**
       * Update a specific team
       */
      updateTeam: (cohortId, team) => {
        console.log('[TeamsStore] Updating team:', cohortId);
        set(
          (state) => ({
            teams: {
              ...state.teams,
              [cohortId]: stampInfluenceSeconds(team, currentSeconds()),
            },
          }),
          false,
          'updateTeam'
        );
      },

      /**
       * Update a team's cash, knowledge points, and influence by team_id
       */
      updateTeamResources: (teamId, newCash, newInfluence, lifetimePassengers, passengerMiles, influenceSeconds) => {
        console.log('[TeamsStore] Updating team resources:', teamId, '→ cash:', newCash, 'influence:', newInfluence);
        set(
          (state) => {
            const updatedTeams = { ...state.teams };
            for (const cohortId of Object.keys(updatedTeams)) {
              if (updatedTeams[cohortId].team_id === teamId) {
                updatedTeams[cohortId] = {
                  ...updatedTeams[cohortId],
                  cash: newCash,
                  influence: newInfluence,
                  ...(lifetimePassengers !== undefined && { lifetime_passengers: lifetimePassengers }),
                  ...(passengerMiles !== undefined && { lifetime_passenger_miles: passengerMiles }),
                  ...(influenceSeconds !== undefined && { influence_seconds: influenceSeconds, influence_seconds_at: currentSeconds() }),
                };
                break;
              }
            }
            return { teams: updatedTeams };
          },
          false,
          'updateTeamResources'
        );
      },

      /**
       * Reset the teams store to initial state
       */
      reset: () => {
        set(initialState, false, 'reset');
      },
    }),
    {
      name: 'teams-store',
    }
  )
);

// ==================== HANDLERS ====================

/** Every team, as a players list carries them on a join. */
export function handleTeamsList(teams: Record<string, Team>) {
  console.log('[TeamsHandler] Setting teams from a players list:', Object.keys(teams).length);
  useTeamsStore.getState().setTeams(teams);
}

/**
 * Handle team update from various messages
 * Used when an action updates the team's resources
 */
export function handleTeamUpdate(cohortId: string, team: Team) {
  console.log('[TeamsHandler] Updating team:', cohortId);
  useTeamsStore.getState().updateTeam(cohortId, team);
}

/**
 * A team's resources as the financial report carries them (financials.ts):
 * its cash and influence, and its lifetime passengers, passenger miles and
 * influence seconds where the report has them. Knowledge points are each
 * player's (players.ts).
 */
export function handleTeamResourcesUpdate(
  teamId: number,
  cash: number,
  influence: number,
  lifetimePassengers?: number,
  passengerMiles?: number,
  influenceSeconds?: number,
): void {
  useTeamsStore.getState().updateTeamResources(teamId, cash, influence, lifetimePassengers, passengerMiles, influenceSeconds);
}

/** Influence as it accrues (OpCodeInfluenceState, influence-clock.go): each team's figures, by cohort. */
export interface InfluenceStateMessage {
  type: typeof MessageType.MessageTypeInfluenceState;
  teams: Record<string, { influence: number; influence_seconds: number; accruing: boolean }>;
}

/**
 * Authoritative active-time progression (influence-clock.go): sent when a
 * team's points or whether it accrues change, on joining, and every
 * InfluenceResyncSeconds besides. The seconds between are counted here
 * (influenceSecondsNow), as the quarter's ring counts to the next update.
 */
export function handleInfluenceState(data: InfluenceStateMessage) {
  const store = useTeamsStore.getState();
  const at = currentSeconds();
  for (const [cohortId, progress] of Object.entries(data.teams)) {
    const team = store.teams[cohortId];
    if (team) {
      store.updateTeam(cohortId, {
        ...team,
        influence: progress.influence,
        influence_seconds: progress.influence_seconds,
        influence_seconds_at: at,
        influence_accruing: progress.accruing,
      });
    }
  }
}

/**
 * The team's seconds toward its next Influence point at `now` (Unix seconds):
 * the server's count, counted on since it was read while the team accrues,
 * and held at the interval until the server's award comes. Paused, it is the
 * server's count as it stands.
 */
export function influenceSecondsNow(team: Pick<Team, 'influence_seconds' | 'influence_seconds_at' | 'influence_accruing'>, now: number, intervalSeconds: number): number {
  const seconds = team.influence_seconds ?? 0;
  if (team.influence_accruing === false || team.influence_seconds_at === undefined) return seconds;
  return Math.min(intervalSeconds, seconds + Math.max(0, now - team.influence_seconds_at));
}
